import {
  activityEntriesForDisplay,
  assistantTextFromEvents,
  compactActivitySummary,
  decisionStatusLabel,
  normalizeDecision,
  runReadableResult,
  sortDecisionsAscending,
  threadWarningFromRun
} from "../../domain/conversation.js";
import { renderDecisionStack } from "../../render/decision-cards.js";
import { renderActivityTimeline } from "../../render/activity-timeline.js";
import {
  escapeHTML,
  formatTimestamp,
  renderEmptyState,
  renderMessageContent,
  truncateIDText
} from "../../render/ui-helpers.js";
import {
  activeThreadTurnFromState,
  latestPendingDecisionForTurn,
  lookupDecisionTurn,
  threadProgressLines,
  threadSendBlockReason,
  threadTraceEntries,
  threadTurnStatusText
} from "../state.js";

export function renderHarnessThreadComponent(state, options = {}) {
  const current = state.thread;
  const activeTurn = activeThreadTurnFromState(state);
  const sendBlockReason = threadSendBlockReason(state, current, activeTurn);
  const canSendMessage = sendBlockReason === "";
  const turns = buildHarnessThreadTurns(state);
  const pendingDecision = activeTurn ? latestPendingDecisionForTurn(state, activeTurn.id) : null;
  const isRecovery = current.state === "recovery_required";
  const hasActiveTurn = Boolean(activeTurn && activeTurn.state && (activeTurn.state === "queued" || activeTurn.state === "running"));
  const isArchived = current.state === "archived" || current.state === "merged";
  const composerHint = state.submitting ? "queueing turn..." : ((!canSendMessage && !hasActiveTurn) ? sendBlockReason : "");
  const agentLabel = String(options.agentLabel || current.agent_id || "unknown");
  const workspaceLabel = String(options.workspaceLabel || current.workspace_id || "unknown");
  const mergedSuffix = String(options.metaSuffix || "");
  const toolbarActionsHTML = String(options.toolbarActionsHTML || "");
  const afterToolbarHTML = String(options.afterToolbarHTML || "");
  const replaceLabel = isRecovery ? (options.recoveryActionLabel || "Recover") : (options.replaceActionLabel || "Fresh thread");

  return `
    <div class="thread-component${options.embed ? " thread-component--embed" : ""}" data-thread-component>
      <div class="thread-toolbar">
        <div class="thread-toolbar__left">
          <span class="badge badge--${escapeHTML(current.state)}">${escapeHTML(current.state)}</span>
          <span class="thread-toolbar__meta">${escapeHTML(agentLabel)} / ${escapeHTML(workspaceLabel)} / ${turns.length} turn${turns.length === 1 ? "" : "s"}${escapeHTML(mergedSuffix)}</span>
          ${pendingDecision ? `<span class="badge badge--decision">${escapeHTML(decisionStatusLabel(pendingDecision, (turnID) => lookupDecisionTurn(state, turnID)))}</span>` : ""}
          <span class="status-dot status-dot--${state.streamState === "connected" ? "ok" : state.streamState === "error" ? "error" : "idle"}" title="Stream: ${escapeHTML(state.streamState)}"></span>
        </div>
        <div class="thread-toolbar__right">
          ${hasActiveTurn ? `<button class="button button--sm button--danger" type="button" data-action="cancel-thread-run">Cancel turn</button>` : ""}
          ${toolbarActionsHTML}
          ${options.allowReplace === false ? "" : `<button class="button button--sm button--ghost" type="button" data-action="replace-thread">${escapeHTML(replaceLabel)}</button>`}
        </div>
      </div>
      ${isRecovery ? `<div class="thread-recovery-banner">${escapeHTML(current.recovery_hint || "Thread continuity was lost. Create a replacement thread to continue.")}</div>` : ""}
      ${afterToolbarHTML}
      <div class="conversation-scroll">
        ${turns.length ? turns.map((item) => renderHarnessThreadTurn(state, item, options)).join("") : renderEmptyState("No messages yet", options.emptyDescription || "Send the first message to start the conversation.")}
      </div>
      ${isArchived ? `<div class="composer-v2 composer-v2--readonly"><div class="composer-v2__hint">${escapeHTML(options.readOnlyMessage || "This thread is read-only. Start a new thread for follow-up work.")}</div></div>` : `
      <form id="thread-message-form" class="composer-v2" data-thread-id="${escapeHTML(current.id)}" data-thread-message-form>
        ${composerHint ? `<div class="composer-v2__hint">${escapeHTML(composerHint)}</div>` : ""}
        <div class="composer-v2__row">
          <textarea name="prompt" placeholder="${escapeHTML(options.composePlaceholder || "Send a message...")}" rows="1">${escapeHTML(state.composerDraft)}</textarea>
          ${hasActiveTurn ? `
            <button class="composer-v2__send composer-v2__send--danger" type="button" data-action="cancel-thread-run" title="Cancel active turn">&times;</button>
          ` : `
            <button class="composer-v2__send" type="submit" ${(canSendMessage && !state.submitting) ? "" : "disabled"} title="Send">${state.submitting ? "..." : "&uarr;"}</button>
          `}
        </div>
      </form>
      `}
    </div>
  `;
}

export function buildHarnessThreadTurns(state) {
  return state.turns.map((item) => {
    const events = state.eventsByTurnID[item.id] || [];
    const decisions = sortDecisionsAscending((state.decisionsByTurnID[item.id] || []).map(normalizeDecision));
    return {
      turn: item,
      events,
      decisions,
      assistantText: assistantTextFromEvents(events),
      result: runReadableResult(item, events),
      activity: activityEntriesForDisplay(events, item),
      progress: threadProgressLines(events),
      trace: threadTraceEntries(events),
      warning: threadWarningFromRun(item, events)
    };
  });
}

export function threadMergeStateSuffix(threadItem, targetBranch) {
  if (!threadItem || (threadItem.state !== "archived" && threadItem.state !== "merged")) {
    return "";
  }
  return ` · Merged into ${targetBranch || "main"}`;
}

function renderHarnessThreadTurn(state, turn, options) {
  const turnState = turn.turn.state;
  const waitingOnDecision = turn.turn.wait_state === "awaiting_decision";
  const showCursor = turnState === "running" && !waitingOnDecision;
  const isWarning = turnState === "applied_with_warning";
  const isFailed = turnState === "failed" || turnState === "canceled";
  const progressCount = turn.progress.length;
  const traceCount = turn.trace.length;
  const isExpanded = state.expandedProgressIDs.has(turn.turn.id);
  const agentModifier = isFailed ? "message-bubble--error" : isWarning ? "message-bubble--warning" : "message-bubble--agent";
  const hasPrompt = turn.turn.prompt && turn.turn.prompt.trim();
  const statusLabel = waitingOnDecision ? "Waiting on decision" : (isFailed || isWarning ? threadTurnStatusText(turnState) : (showCursor ? "Running" : "Completed"));
  const disclosureLabel = progressCount > 0 ? `Show ${progressCount} progress step${progressCount === 1 ? "" : "s"}` : "Show raw trace";
  const showProgressDetails = options.showProgressDetails !== false;
  const showTraceDetails = options.showTraceDetails !== false;
  const showLinks = options.showTurnLinks !== false;
  const turnHref = typeof options.turnHref === "function" ? options.turnHref(turn.turn.id) : `/turns/${encodeURIComponent(turn.turn.id)}`;
  const activity = turn.activity || [];
  const showActivity = activity.length > 0;
  const isLive = turnState === "running" && !waitingOnDecision;
  const resultText = turn.assistantText ? turn.result : (showActivity && turnState === "running" ? "Live activity in progress..." : turn.result);

  return `
    <div class="message-group">
      ${hasPrompt ? `
        <div class="message-bubble message-bubble--user">
          <div class="message-bubble__body">${renderMessageContent(turn.turn.prompt)}</div>
        </div>
      ` : ""}
      <div class="message-turn-sep">
        Agent &middot; ${escapeHTML(statusLabel)} &middot; ${escapeHTML(formatShortTime(turn.turn.created_at))}
        ${showLinks ? `<a href="${escapeHTML(turnHref)}" data-route title="View turn trace">#${escapeHTML(truncateIDText(turn.turn.id, 6))}</a>` : ""}
      </div>
      <div class="message-bubble ${agentModifier}">
        ${showLinks ? `<a class="message-bubble__trace-hover" href="${escapeHTML(turnHref)}" data-route title="View turn trace">trace</a>` : ""}
        <div class="message-bubble__body">${renderMessageContent(resultText || "Waiting for response...")}${showCursor ? `<span class="stream-cursor" aria-hidden="true">&#9613;</span>` : ""}</div>
        ${showActivity ? renderHarnessThreadTurnActivity(activity, turnState, isLive) : ""}
        ${turn.warning ? `<div class="form-note form-note--warn" style="margin-top:var(--space-sm)">${escapeHTML(turn.warning)}</div>` : ""}
        ${turn.decisions.length ? renderHarnessThreadTurnDecisions(state, turn) : ""}
        ${showProgressDetails || showTraceDetails ? renderHarnessThreadTurnDisclosures(state, turn, { disclosureLabel, progressCount, traceCount, isExpanded, showProgressDetails, showTraceDetails }) : ""}
      </div>
    </div>
  `;
}

function renderHarnessThreadTurnActivity(items, _turnState, isLive) {
  if (isLive) {
    return renderActivityTimeline(items, { live: true, limit: 6 });
  }
  if (!compactActivitySummary(items)) return "";
  return `
    <details class="disclosure">
      <summary>Show activity timeline</summary>
      <div class="disclosure__body">
        ${renderActivityTimeline(items)}
      </div>
    </details>
  `;
}

function renderHarnessThreadTurnDisclosures(state, turn, options) {
  const { disclosureLabel, progressCount, traceCount, isExpanded, showProgressDetails, showTraceDetails } = options;
  if ((!showProgressDetails || progressCount === 0) && (!showTraceDetails || traceCount === 0)) {
    return "";
  }
  return `
    <details class="disclosure" ${isExpanded ? "open" : ""}>
      <summary data-action="toggle-progress" data-turn-id="${escapeHTML(turn.turn.id)}">${escapeHTML(disclosureLabel)}</summary>
      <div class="disclosure__body">
        ${showProgressDetails ? turn.progress.map((item) => `<div>${escapeHTML(item)}</div>`).join("") : ""}
        ${showTraceDetails && traceCount > 0 ? `
          <div class="trace-stack">
            <div class="trace-stack__label">Raw trace</div>
            <div class="trace-stack__list">
              ${turn.trace.map((item) => `
                <article class="trace-card">
                  <div class="trace-card__header">${escapeHTML(item.eventType)}</div>
                  <pre class="trace-card__body">${escapeHTML(item.body)}</pre>
                </article>
              `).join("")}
            </div>
          </div>
        ` : ""}
      </div>
    </details>
  `;
}

function renderHarnessThreadTurnDecisions(state, turn) {
  return `
    <div class="decision-stack">
      ${renderDecisionStack(turn.decisions, {
    compact: true,
    decisionDrafts: state.decisionDrafts,
    escapeHTML,
    formatTimestamp,
    lookupTurn: (turnID) => lookupDecisionTurn(state, turnID),
    threadID: turn.turn.thread_id,
    turnID: turn.turn.id
  })}
    </div>
  `;
}

function formatShortTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
